// Hitstop (§2.13): presentation-only freezes. The simulation never stops; the renderer holds the
// involved beans' rendered pose for 70 ms (120 ms on heavy hits, 150 ms on the round's final KO hit,
// 90 ms on a CLASH), then catches them up to their true interpolated positions. When the local bean
// is the victim the freeze starts the moment the HIT arrives, so the prediction's reconciliation
// snap (often metres at 120 ms of lag) happens while the pose is frozen and is never seen.
//
// The catch-up is a speed-limited residual: after the freeze the presented pose is the true pose
// plus a residual that shrinks by at most `maxExcessSpeed` (9 m/s) and that also absorbs any jump of
// the true pose its own velocity does not explain (the prediction's decaying visual offset, a second
// correction). The presented bean therefore never moves more than 0.15 m per 60 Hz frame beyond its
// own motion, under the 0.2 m "visible pop" bar of the WP7 acceptance. DOM- and GL-free.
import { MAX_BEANS } from '@shared/constants';
import { BS } from '@shared/ids';
import type { BeanView } from '../../game/FrameView';

export const HITSTOP = {
  normalMs: 70,
  heavyMs: 120,
  finalMs: 150,
  clashMs: 90,
  heavyDizzy: 85,
  /** §2.13: the catch-up takes at least 60 ms. */
  catchupMs: 60,
  /** Extra apparent speed (m/s) the catch-up may add on top of the bean's own motion. */
  maxExcessSpeed: 9,
  /** Corrections larger than this are teleports (respawn, round change) and snap. */
  snapM: 8,
  /** The local bean's unexplained jumps above this (m) are smoothed too, hit or not; larger ones snap. */
  localSnapM: 4,
  /** The residual is dropped below this (m). */
  settleM: 0.004,
  /** "Both flash white 1 frame (33 ms)." */
  flashMs: 33,
} as const;

/** Copies every BeanView field (cosmetics by reference: the source owns that object). */
export function copyBean(dst: BeanView, src: BeanView): void {
  dst.slot = src.slot; dst.active = src.active; dst.isLocal = src.isLocal; dst.isBot = src.isBot; dst.connected = src.connected;
  dst.name = src.name; dst.identity = src.identity; dst.team = src.team; dst.cosmetics = src.cosmetics; dst.voicePitch = src.voicePitch;
  dst.state = src.state; dst.st = src.st; dst.x = src.x; dst.y = src.y; dst.z = src.z; dst.vx = src.vx; dst.vy = src.vy; dst.vz = src.vz;
  dst.facing = src.facing; dst.radius = src.radius; dst.dizzy = src.dizzy; dst.item = src.item; dst.charges = src.charges;
  dst.out = src.out; dst.ghost = src.ghost; dst.hasPotato = src.hasPotato; dst.holdsCrown = src.holdsCrown; dst.crowned = src.crowned;
  dst.big = src.big; dst.floating = src.floating; dst.invuln = src.invuln; dst.partner = src.partner;
  dst.edgeX = src.edgeX; dst.edgeY = src.edgeY; dst.edgeNx = src.edgeNx; dst.edgeNy = src.edgeNy;
  dst.place = src.place; dst.crownTicks = src.crownTicks; dst.readyTicks = src.readyTicks; dst.score = src.score;
  dst.ledgeCd = src.ledgeCd;
}

/** True when a bean has just entered a rim state (TEETER / LEDGE), where the sim snaps it onto the edge. */
function rimSnap(s: BeanView, prev: number): boolean {
  return (s.state === BS.TEETER || s.state === BS.LEDGE) && prev !== s.state && prev !== -1;
}

export class Hitstop {
  private readonly freezeMs = new Float64Array(MAX_BEANS);
  private readonly flashLeft = new Float64Array(MAX_BEANS);
  /** 1 while a residual is being worked off. */
  private readonly limiting = new Uint8Array(MAX_BEANS);
  /** Ms since the freeze ended (the §2.13 minimum catch-up). */
  private readonly sinceFreeze = new Float64Array(MAX_BEANS);
  private readonly frozen = new Float64Array(MAX_BEANS * 3);
  private readonly residual = new Float64Array(MAX_BEANS * 3);
  /** Last presented pose, and the source pose of the last frame. */
  private readonly last = new Float64Array(MAX_BEANS * 3);
  private readonly lastSrc = new Float64Array(MAX_BEANS * 3);
  private readonly hasLast = new Uint8Array(MAX_BEANS);
  private readonly lastState = new Int8Array(MAX_BEANS).fill(-1);

  /** Freezes `slot` for at least `ms` from now, holding its last presented position. */
  freeze(slot: number, ms: number): void {
    if (slot < 0 || slot >= MAX_BEANS || !this.hasLast[slot]) return;
    if (this.freezeMs[slot] <= 0) {
      const o = slot * 3;
      this.frozen[o] = this.last[o];
      this.frozen[o + 1] = this.last[o + 1];
      this.frozen[o + 2] = this.last[o + 2];
    }
    this.freezeMs[slot] = Math.max(this.freezeMs[slot], ms);
  }

  /** White flash for `ms` (Reduced Flashing callers skip it). */
  flash(slot: number, ms: number = HITSTOP.flashMs): void {
    if (slot >= 0 && slot < MAX_BEANS) this.flashLeft[slot] = Math.max(this.flashLeft[slot], ms);
  }

  /** Remaining freeze of `slot` in ms (BeanRig.update holds its pose while > 0). */
  ms(slot: number): number {
    return this.freezeMs[slot] ?? 0;
  }

  flashing(slot: number): boolean {
    return (this.flashLeft[slot] ?? 0) > 0;
  }

  /** True while `slot` is frozen or catching up. */
  active(slot: number): boolean {
    return this.freezeMs[slot] > 0 || this.limiting[slot] === 1;
  }

  reset(): void {
    this.freezeMs.fill(0);
    this.flashLeft.fill(0);
    this.limiting.fill(0);
    this.hasLast.fill(0);
  }

  /**
   * Writes the presented beans: copies of `src` whose positions are frozen or caught up. `dtMs` is
   * real time (slow motion must not stretch hitstop). `smoothSlot` (the local bean) also has any
   * other unexplained jump of its true pose (a reconciliation without a hit) worked off the same way.
   */
  present(src: readonly BeanView[], out: BeanView[], dtMs: number, smoothSlot = -1): void {
    const dt = dtMs / 1000;
    for (let slot = 0; slot < MAX_BEANS; slot++) {
      const s = src[slot];
      const b = out[slot];
      copyBean(b, s);
      const o = slot * 3;
      this.flashLeft[slot] = Math.max(0, this.flashLeft[slot] - dtMs);
      if (!s.active) {
        this.freezeMs[slot] = 0;
        this.limiting[slot] = 0;
        this.hasLast[slot] = 0;
        this.lastState[slot] = -1;
        continue;
      }
      const r = this.residual;
      if (this.freezeMs[slot] > 0) {
        this.freezeMs[slot] = Math.max(0, this.freezeMs[slot] - dtMs);
        b.x = this.frozen[o];
        b.y = this.frozen[o + 1];
        b.z = this.frozen[o + 2];
        if (this.freezeMs[slot] === 0) {
          r[o] = this.frozen[o] - s.x;
          r[o + 1] = this.frozen[o + 1] - s.y;
          r[o + 2] = this.frozen[o + 2] - s.z;
          const d = Math.hypot(r[o], r[o + 1], r[o + 2]);
          this.limiting[slot] = d > HITSTOP.settleM && d < HITSTOP.snapM ? 1 : 0;
          this.sinceFreeze[slot] = 0;
        }
      } else if (!this.limiting[slot] && (slot === smoothSlot || rimSnap(s, this.lastState[slot])) && this.hasLast[slot] && dt > 0) {
        // A correction of the local bean, or any bean snapped onto a rim (a vanishing-floor scramble:
        // the sim moves it up to 0.9 m onto the edge in one tick, INT-A): start working it off if it
        // would show as a pop, so it scrambles over to the rim in ~0.1 s instead.
        const ux = s.x - this.lastSrc[o] - s.vx * dt;
        const uy = s.y - this.lastSrc[o + 1] - s.vy * dt;
        const uz = s.z - this.lastSrc[o + 2] - s.vz * dt;
        const u = Math.hypot(ux, uy, uz);
        if (u > HITSTOP.maxExcessSpeed * dt && u < HITSTOP.localSnapM) {
          r[o] = -ux;
          r[o + 1] = -uy;
          r[o + 2] = -uz;
          this.limiting[slot] = 1;
          this.sinceFreeze[slot] = HITSTOP.catchupMs;
          const step = Math.min(u, HITSTOP.maxExcessSpeed * dt);
          const k = (u - step) / u;
          r[o] *= k;
          r[o + 1] *= k;
          r[o + 2] *= k;
          b.x = s.x + r[o];
          b.y = s.y + r[o + 1];
          b.z = s.z + r[o + 2];
        }
      } else if (this.limiting[slot]) {
        // Absorb source motion the bean's own velocity does not explain (offset decay, corrections).
        if (this.hasLast[slot]) {
          r[o] -= s.x - this.lastSrc[o] - s.vx * dt;
          r[o + 1] -= s.y - this.lastSrc[o + 1] - s.vy * dt;
          r[o + 2] -= s.z - this.lastSrc[o + 2] - s.vz * dt;
        }
        this.sinceFreeze[slot] += dtMs;
        const d = Math.hypot(r[o], r[o + 1], r[o + 2]);
        if (d > HITSTOP.snapM) {
          this.limiting[slot] = 0;
        } else {
          // Shrink by at most the excess speed, and never faster than the §2.13 60 ms catch-up.
          const left = Math.max(1, HITSTOP.catchupMs - this.sinceFreeze[slot] + dtMs);
          const step = Math.min(d, HITSTOP.maxExcessSpeed * dt, d * Math.min(1, dtMs / left));
          const k = d > 0 ? (d - step) / d : 0;
          r[o] *= k;
          r[o + 1] *= k;
          r[o + 2] *= k;
          if (d - step <= HITSTOP.settleM) this.limiting[slot] = 0;
          else {
            b.x = s.x + r[o];
            b.y = s.y + r[o + 1];
            b.z = s.z + r[o + 2];
          }
        }
      }
      this.lastSrc[o] = s.x;
      this.lastSrc[o + 1] = s.y;
      this.lastSrc[o + 2] = s.z;
      this.lastState[slot] = s.state;
      this.last[o] = b.x;
      this.last[o + 1] = b.y;
      this.last[o + 2] = b.z;
      this.hasLast[slot] = 1;
    }
  }
}
